import { ArrowBack, CheckCircle, Schedule, RadioButtonUnchecked } from '@mui/icons-material';
import { Chip, IconButton, Stack, Tab, Tabs } from '@mui/material';
import { ShowBase, Title, useShowContext, useCanAccess as raUseCanAccess } from 'react-admin';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { ResourceNotFound } from '@/components/misc/ResourceNotFound';
import { ProjectSummaryTab } from './ProjectSummaryTab';
import { ProjectPermitsTab } from './ProjectPermitsTab';
import { ProjectReceiptsTab } from './ProjectReceiptsTab';
import { useBrowserTitle } from '@/utils/useBrowserTitle';
import { useProjectFlow } from './flow/useProjectFlow';
import { PhaseStepsPanel } from './flow/PhaseStepsPanel';
import { FieldsTimeline } from './flow/FieldsTimeline';
import { ContainerPhasePanel } from './flow/ContainerPhasePanel';
import { ContractSigningPanel } from './contract/ContractSigningPanel';
import { ContractSignatureChips } from './contract/ContractSignatureChips';

export const ProjectShow = () => (
    <ShowBase resource="projects">
        <ProjectShowContent />
    </ShowBase>
);

type TabValue = 'summary' | 'contract' | 'permits' | 'fields' | 'warranty' | 'maintenance' | 'receipts';

const ProjectShowContent = () => {
    const { record, isPending, error } = useShowContext();
    const [tab, setTab] = React.useState<TabValue>('summary');
    const { canAccess: canReadFlow } = raUseCanAccess({
        resource: 'project_flow',
        action: 'show',
    });

    const isActive = record?.phase === 'active';
    const isCompleted = record?.phase === 'completed';
    // Flow panel renders for active (editable) and completed (read-only) projects
    const flowEnabled = (isActive || isCompleted) && canReadFlow;
    const { data: flow, isLoading: flowLoading, refetch: refetchFlow } = useProjectFlow(
        record?.id,
        flowEnabled
    );

    useBrowserTitle(
        record?.project_no != null ? `${record.project_no} - Project` : undefined,
    );

    if (isPending) return null;
    if (error || !record) {
        return (
            <ResourceNotFound
                resourceLabel="Project"
                listPath="/projects"
                listLabel="Back to projects"
            />
        );
    }

    const getPhaseStatus = (phaseKey: string) => {
        return flow?.phases.find(p => p.key === phaseKey)?.status;
    };

    // Colored status icon shown at the end of each flow tab.
    const statusTabIcon = (status?: string) => {
        switch (status) {
            case 'done':
                return <CheckCircle color="success" fontSize="small" />;
            case 'in_progress':
                return <Schedule color="primary" fontSize="small" />;
            case 'pending':
                return <RadioButtonUnchecked color="disabled" fontSize="small" />;
            default:
                return undefined;
        }
    };

    return (
        <Stack gap={2} p={1} sx={{ width: '100%', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={1}>
                <IconButton component={Link} to="/projects" aria-label="Back to projects">
                    <ArrowBack />
                </IconButton>
                <Title title="Project" />
                {isCompleted && (
                    <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircle />}
                        label="Completed"
                    />
                )}
            </Stack>

            <Tabs
                value={tab}
                onChange={(_, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ px: 1 }}
            >
                <Tab label="Summary" value="summary" />
                {flowEnabled && (
                    <Tab
                        label="Contract"
                        value="contract"
                        icon={statusTabIcon(getPhaseStatus('contract'))}
                        iconPosition="end"
                    />
                )}
                {flowEnabled && (
                    <Tab
                        label="Permits"
                        value="permits"
                        icon={statusTabIcon(getPhaseStatus('permits'))}
                        iconPosition="end"
                    />
                )}
                {flowEnabled && (
                    <Tab
                        label="Fields"
                        value="fields"
                        icon={statusTabIcon(getPhaseStatus('fields'))}
                        iconPosition="end"
                    />
                )}
                {flowEnabled && (
                    <Tab
                        label="Warranty"
                        value="warranty"
                        icon={statusTabIcon(getPhaseStatus('warranty'))}
                        iconPosition="end"
                    />
                )}
                {flowEnabled && (
                    <Tab
                        label="Maintenance"
                        value="maintenance"
                        icon={statusTabIcon(getPhaseStatus('maintenance'))}
                        iconPosition="end"
                    />
                )}
                {!flowEnabled && <Tab label="Permits" value="permits" />}
                <Tab label="Receipts" value="receipts" />
            </Tabs>

            {tab === 'summary' && <ProjectSummaryTab />}
            {!flowEnabled && tab === 'permits' && <ProjectPermitsTab />}
            {flowEnabled && tab === 'contract' && flow && (
                <Stack gap={0}>
                    <PhaseStepsPanel
                        projectId={record.id}
                        phase={flow.phases.find(p => p.key === 'contract')!}
                        currentUserId={record.id}
                        hideHeader
                        stepFooter={step =>
                            step.template_key === 'contract.signed' ? (
                                <ContractSignatureChips projectId={record.id} />
                            ) : null
                        }
                    />
                    <ContractSigningPanel projectId={record.id} onChanged={refetchFlow} />
                </Stack>
            )}
            {flowEnabled && tab === 'permits' && flow && (
                <Stack gap={2}>
                    <PhaseStepsPanel
                        projectId={record.id}
                        phase={flow.phases.find(p => p.key === 'permits')!}
                        currentUserId={record.id}
                        hideHeader
                    />
                    <ProjectPermitsTab />
                </Stack>
            )}
            {flowEnabled && tab === 'fields' && flow && (
                <FieldsTimeline
                    projectId={record.id}
                    phase={flow.phases.find(p => p.key === 'fields')!}
                    currentUserId={record.id}
                />
            )}
            {flowEnabled && tab === 'warranty' && flow && (
                <ContainerPhasePanel
                    projectId={record.id}
                    phase={flow.phases.find(p => p.key === 'warranty')!}
                    currentUserId={record.id}
                />
            )}
            {flowEnabled && tab === 'maintenance' && flow && (
                <ContainerPhasePanel
                    projectId={record.id}
                    phase={flow.phases.find(p => p.key === 'maintenance')!}
                    currentUserId={record.id}
                />
            )}
            {tab === 'receipts' && <ProjectReceiptsTab />}
        </Stack>
    );
};
